import React, { useRef, useEffect, useState } from 'react'
import styled from 'styled-components'
import { SignalHandler } from 'badmfck-signal'
import GD from '../GD'
import { IconButton } from '../components/IconButton'
import { Grid } from '../components/Grid'
import { Add, Send } from '../components/Icons'
import {
  appendCodeLine, getCursorPosition, insertStringInString, moveCursor, updateCodeIndexes, setCursorNearCode,
} from '../utils/Input'

const Root = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  padding: 10px 18px;
  width: 100%;
  background: ${props => props.theme.color.primary};
`
const InputContainer = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  min-height: 51px;
`
const Input = styled.div`
  flex-grow: 1;
  margin: 4px 10px 4px 10px;
  text-align: left;
  line-height: 18px;
  max-height: 200px;
  font-size: 16px;
  color: ${props => props.theme.color.main.inputText};
  white-space: pre-wrap;
  overflow-x: hidden;
  overflow-y: auto;
  overflow-wrap: anywhere;
  & code {
    display: flex;
    flex-direction: column;
    padding: 10px;
    max-height: 60vh;
    white-space: pre;
    font-family: monospace;
    font-size: 13px;
    color: #3bffd1;
    background-color: #0f2a38;
    border-radius: 3px;
    overflow-x: auto;
    
    & div {
      position: relative;
      display: flex;
      align-items: center;
      padding-left: 40px;
      margin-bottom: 2px;
      height: 24px;
      &::before {
        position: absolute;
        left: 0;
        display: block;
        content: attr(data-n);
        margin-right: 8px;
        padding: 3px;
        text-align: center;
        width: 25px;
        min-width: 25px;
        color: rgba(255, 255, 255, 0.5);
        background-color: rgba(0, 0, 0, 0.2);
        border-right: 1px solid rgba(0, 0, 0, 0.6);
      }
    }
  }
  
  &:focus {
    outline: none;
  }
  &[data-prompt="true"] {
    opacity: 0.7;
    color: ${props => props.theme.color.main.inputPlaceholder};
    font-style: italic;
  }
`

let inputChanged = false

export default () => {
  const ref = useRef<HTMLInputElement>(null)
  const file = useRef<HTMLInputElement>(null)
  
  const [chatVO, setChatVO] = useState<ChatVO | null>(null)
  const [selectedText, setSelectedText] = useState<string>()

  useEffect(() => {
    const sh = new SignalHandler()

    sh.add(GD.SET_CHAT, (cvo) => {
      setChatVO(cvo)
    })

    return () => {
      sh.clear()
    }
  }, [])

  const onAttachClick = () => {
    if (!file.current) return
    file.current.focus()
    file.current.click()
  }

  const onFileUpload = (e: React.ChangeEvent) => {
    const fileList: FileList | null = (e.target as HTMLInputElement).files

    if (!fileList) return

    const files: File[] = []
    const l: number = fileList.length
    for (let i = 0; i < l; i++) {
      const f = fileList.item(i)
      if (f) files.push(f)
    }

    if (l > 10) {
      alert('You can select up to 10 files')
      return
    }

    GD.SEND_FILES.invoke(files);
    (e.target as HTMLInputElement).value = ''
  }

  const onInput = () => {
    if (!ref.current) return

    if (ref.current.textContent && ref.current.textContent.length > 0) {
      inputChanged = true
    }
    else {
      inputChanged = false
      setSelectedText(undefined)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!ref.current) return

    switch (e.key) {
      case 'Enter': {
        if (e.shiftKey) {
          const focusNode = window.getSelection()?.focusNode
          const codeLineEl = focusNode?.parentElement
          const codeLineIndex = codeLineEl?.getAttribute('data-n')

          const isCodeLine = codeLineEl?.hasAttribute('data-n')
          const isEmptyCodeLine = codeLineEl?.tagName === 'CODE'

          if (isCodeLine && codeLineEl && codeLineIndex) {
            e.preventDefault()
            appendCodeLine(codeLineEl, codeLineIndex)
            updateCodeIndexes()
          }
          else if (isEmptyCodeLine && focusNode) {
            e.preventDefault()
            const focusNodeIndex = (focusNode as HTMLElement)?.getAttribute('data-n') as string
            appendCodeLine(focusNode as HTMLElement, focusNodeIndex)
            updateCodeIndexes()
          }
        }
        if (!(e.shiftKey || e.altKey || e.ctrlKey)) {
          e.preventDefault()
          send()
        }
        break
      }
      case 'Tab': {
        e.preventDefault()

        const cursorPosition = getCursorPosition()
        const tab = '    '

        if (cursorPosition || cursorPosition === 0) {
          ref.current.innerHTML = insertStringInString(ref.current.innerHTML, tab, cursorPosition)
          moveCursor(ref.current, cursorPosition + tab.length)
        }
        break
      }
      case 'ArrowUp': {
        const codeLines = ref.current.querySelectorAll('code div')

        if (codeLines) {
          setCursorNearCode(ref.current, codeLines, 'up')
        }
        break
      }
      case 'ArrowDown': {
        const codeLines = ref.current.querySelectorAll('code div')

        if (codeLines) {
          setCursorNearCode(ref.current, codeLines, 'down')
        }
        break
      }
    }
  }

  const handleKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!ref.current) return

    switch (e.key) {
      case 'Backspace': {
        const focusNode = window.getSelection()?.focusNode
        const codeLineEl = focusNode?.parentElement
        const isEmptyCodeLine = codeLineEl?.tagName === 'CODE'

        if (isEmptyCodeLine) {
          updateCodeIndexes()
        }
        break
      }
    }
  }

  const onFocus = () => {
    if (!ref.current) return
    GD.SEND_INPUT_ACTIVITY.invoke({ isTyping: true })

    const activePlaceholder = ref.current.getAttribute('data-prompt') === 'true'

    if (!inputChanged || activePlaceholder) {
      ref.current.removeAttribute('data-prompt')
      ref.current.textContent = ''
    }
  }

  const onBlur = () => {
    if (!ref.current) return
    GD.SEND_INPUT_ACTIVITY.invoke({ isTyping: false })

    if (ref.current.textContent && ref.current.textContent.length === 0) inputChanged = false
    if (!inputChanged) {
      ref.current.textContent = window.DCC_SHARED.promptMessage
      ref.current.setAttribute('data-prompt', 'true')
    }
  }

  const onPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()

    if (!ref.current) return
    if (!event.clipboardData) return

    let txt = event.clipboardData.getData('text/plain') // только plain text

    // проверка на файлы
    for (const item of event.clipboardData.items) {
      if (item.kind === 'file') {
        const file = item.getAsFile()
        if (file) {
          GD.SEND_FILES.invoke([file])
        }
      }
    }

    // пример обработки "кодового" текста
    let isCode = false
    for (const item of event.clipboardData.items) {
      if (
        item.type.indexOf('text/rtf') !== -1 ||
        item.type.indexOf('editor') !== -1
      ) {
        isCode = true
        break
      }
    }

    if (isCode) {
      txt = '{code}' + txt + '{code}'
    }

    inputChanged = true

    const selection = window.getSelection()
    if (!selection || !selection.rangeCount) return

    const range = selection.getRangeAt(0)

    // создаём фрагмент только с текстом
    const fragment = document.createDocumentFragment()
    txt.split('\n').forEach((line, index) => {
      if (index > 0) {
        fragment.appendChild(document.createElement('br'))
      }
      fragment.appendChild(document.createTextNode(line))
    })

    range.deleteContents()
    range.insertNode(fragment)

    // поставить курсор в конец вставки
    let { endContainer, endOffset } = range
    if (
      endContainer.nodeType === Node.TEXT_NODE &&
      endContainer.textContent?.length === range.endOffset
    ) {
      if (endContainer.nextSibling) {
        endContainer = endContainer.nextSibling
        endOffset = 0
      }
    }

    range.setStart(endContainer, endOffset)
    range.collapse(true)
    selection.removeAllRanges()
    selection.addRange(range)
  }

  const send = () => {
    if (!ref.current) return

    let txt = ref.current.innerHTML

    if (txt.trim().length === 0) return

    txt = txt.replace('\t', 'TAB')
    txt = txt.replaceAll('<br>', '\n')
    txt = txt.replace(/\r/g, '')

    ref.current.textContent = txt
    txt = ref.current.textContent

    const translate: IObjectData = {
      '&nbsp;': ' ',
      '&amp;': '&',
      '&quot;': '"',
      '&lt;': '<',
      '&gt;': '>',
    }
    for (const i in translate) txt = txt.replaceAll(i, translate[i])

    if (!txt) {
      GD.LOGE.invoke('No text to send')
      return
    }

    if (!inputChanged) {
      GD.LOGE.invoke('Input wasn\'t changed')
      return
    }

    if (txt.length > 2000) {
      GD.ALERT_ERROR.invoke('Message too big!')
      return
    }

    inputChanged = false

    ref.current.textContent = ''
    ref.current.focus()

    if (selectedText) setSelectedText(undefined)

    GD.CHAT_MESSAGE_SEND_REQUEST.invoke(txt)
  }

  if (!chatVO) return null

  return (
    <Root>
      <InputContainer>
        <IconButton
          variant="outlined"
          icon={<Add />}
          color="secondary"
          onClick={onAttachClick}
        />

        <Grid direction="column" gap={10} fullwidth>
          <Grid align="center" justify="space-between" gap={15} fullwidth noWrap>
            <Input
              ref={ref}
              onFocus={onFocus}
              data-prompt={true}
              onBlur={onBlur}
              onInput={onInput}
              onKeyDown={handleKeyDown}
              onKeyUp={handleKeyUp}
              onPaste={onPaste}
              contentEditable={!!chatVO}
              suppressContentEditableWarning={true}
            >
              {window.DCC_SHARED.promptMessage}
            </Input>
            <IconButton variant="contained" icon={<Send />} size="l" color="swiss" onClick={send} />
          </Grid>
        </Grid>
      </InputContainer>
      <input ref={file} multiple style={{ display: 'none' }} type="file" onChange={onFileUpload} onPaste={onPaste} />
    </Root>
  )
}
